import { useState, useEffect } from 'react';
import { Box, Card, Typography, Grid, Paper, Stack, Autocomplete, TextField, MenuItem, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { evVehicleOptions, generateEvData } from './dummyEv';
import { AreaChart, Area, BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../../utils/i18n';
import BatteryCharging20Icon from '@mui/icons-material/BatteryCharging20';
import BatteryCharging30Icon from '@mui/icons-material/BatteryCharging30';
import BatteryCharging50Icon from '@mui/icons-material/BatteryCharging50';
import BatteryCharging60Icon from '@mui/icons-material/BatteryCharging60';
import BatteryCharging80Icon from '@mui/icons-material/BatteryCharging80';
import BatteryCharging90Icon from '@mui/icons-material/BatteryCharging90';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';

const getBatteryIcon = (level) => {
  if (level <= 20) return <BatteryCharging20Icon fontSize="medium" />;
  if (level <= 30) return <BatteryCharging30Icon fontSize="medium" />;
  if (level <= 50) return <BatteryCharging50Icon fontSize="medium" />;
  if (level <= 60) return <BatteryCharging60Icon fontSize="medium" />;
  if (level <= 80) return <BatteryCharging80Icon fontSize="medium" />;
  if (level <= 90) return <BatteryCharging90Icon fontSize="medium" />;
  return <BatteryChargingFullIcon fontSize="medium" />;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(value);
};

// ---------------------------------------------------------------

const StatBox = ({ title, value, icon, color, iconBg }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 3,
      bgcolor: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
      height: '100%',
      transition: 'transform 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
      }
    }}
  >
    <Typography variant="body2" color="text.secondary" fontWeight="600" mb={1.5}>
      {title}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: iconBg,
          color: color
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" fontWeight="800" color="#1e293b">
        {value}
      </Typography>
    </Stack>
  </Paper>
);

export default function RevenueEvVehicle() {
  const { t } = useLanguage();

  const [selectedVehicle, setSelectedVehicle] = useState(evVehicleOptions[0]);
  const [periodType, setPeriodType] = useState('last_week');

  const [evData, setEvData] = useState({
    avgBatteryLevel: 0,
    batteryHealth: 0,
    batteryTemp: 0,
    chargingEstimation: '00:00:00',
    totalFleetKwh: 0
  });
  const [historyData, setHistoryData] = useState([]);

  // Fungsi untuk handle pencarian data dummy
  const handleSearch = () => {
    if (!selectedVehicle) return;

    let fromDateStr = dayjs('2026-06-01').format('YYYY-MM-DD');
    let toDateStr = dayjs('2026-06-10').format('YYYY-MM-DD');

    // Dummy behavior untuk periodType
    if (periodType === 'last_24_hours') {
      fromDateStr = dayjs('2026-06-09').format('YYYY-MM-DD');
      toDateStr = dayjs('2026-06-10').format('YYYY-MM-DD');
    } else if (periodType === 'last_week') {
      fromDateStr = dayjs('2026-06-04').format('YYYY-MM-DD');
      toDateStr = dayjs('2026-06-10').format('YYYY-MM-DD');
    }

    const { currentStats, periodStats, historyData: newHistory } = generateEvData(selectedVehicle.value, fromDateStr, toDateStr);

    setEvData({
      ...currentStats,
      ...periodStats
    });
    setHistoryData(newHistory);
  };

  // Panggil pertama kali saat render
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', height: '100%' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            bgcolor: '#4c5c96',
            color: 'white',
            borderRadius: 1.5,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ElectricBoltIcon fontSize="small" />
        </Box>
        <Typography variant="h4" fontWeight="bold">
          {t('revenue.electricVehicle', 'Electric Vehicle')}
        </Typography>
      </Box>
      {/* FILTER SECTION */}
      <Box sx={{ mb: 2, p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, md: 4 }}>
            <Autocomplete
              size="small"
              options={evVehicleOptions}
              getOptionLabel={(option) => option.label}
              value={selectedVehicle}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              onChange={(event, newValue) => {
                setSelectedVehicle(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('revenue.electricVehicle', 'Electric Vehicle')}
                  placeholder={t('general.selectVehicle', 'Select Vehicle')}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={t('revenue.period', 'Period')}
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
            >
              <MenuItem value="last_24_hours">{t('time.last24Hours', 'Last 24 Hours')}</MenuItem>
              <MenuItem value="last_week">{t('time.lastWeek', 'Last Week')}</MenuItem>
              <MenuItem value="last_month">{t('time.lastMonth', 'Last Month')}</MenuItem>
              <MenuItem value="custom">{t('time.custom', 'Custom')}</MenuItem>
            </TextField>
          </Grid>
          <Grid item size={{ xs: 12, md: 2 }}>
            <Button variant="contained" onClick={handleSearch} startIcon={<SearchIcon />} fullWidth sx={{ textTransform: 'none' }}>
              {t('general.search', 'Search')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        {/* KIRI: FLEET BATTERY STATUS */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="#0f172a">
                {t('revenue.batteryStatus', 'Fleet Battery Status')} (Dummy)
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <StatBox
                  title={t('revenue.batteryLevel', 'Battery Level')}
                  value={`${evData.avgBatteryLevel}%`}
                  icon={getBatteryIcon(evData.avgBatteryLevel)}
                  color="#10b981" // Emerald Green
                  iconBg="#d1fae5"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <StatBox
                  title={t('revenue.batteryHealth', 'Battery Health')}
                  value={`${evData.batteryHealth} %`}
                  icon={<MonitorHeartIcon fontSize="medium" />}
                  color="#0ea5e9" // Blue
                  iconBg="#e0f2fe"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <StatBox
                  title={t('revenue.batteryTemperature', 'Battery Temperature')}
                  value={`${evData.batteryTemp} ℃`}
                  icon={<DeviceThermostatIcon fontSize="medium" />}
                  color="#f59e0b" // Orange/Warning
                  iconBg="#fef3c7"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <StatBox
                  title={t('revenue.chargingEstimation', 'Charging Estimation')}
                  value={`${evData.chargingEstimation}`}
                  icon={<AccessTimeIcon fontSize="medium" />}
                  color="#8b5cf6" // Purple
                  iconBg="#ede9fe"
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* KANAN: FLEET ENERGY CONSUMPTION */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" fontWeight="bold" color="#0f172a" mb={2}>
              {t('revenue.energyConsumption', ' Fleet Energy Consumption')} (Dummy)
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ flexGrow: 1, width: '100%', minHeight: 150 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={historyData} margin={{ top: 10, right: 0, left: -25, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorKwh" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00e676" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#00e676" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" hide />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 600 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      itemStyle={{ color: '#0f172a', fontWeight: 'bold' }}
                      formatter={(value) => [`${value} kWh`, 'Consumed']}
                    />
                    <Area
                      type="monotone"
                      dataKey="kwh"
                      stroke="#00e676"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorKwh)"
                      activeDot={{ r: 6, fill: '#00e676', stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>

              <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mt={2}>
                <Typography variant="subtitle1" fontWeight="600" color="#64748b">
                  Kwh/trip
                </Typography>
                <Typography variant="h4" fontWeight="800" color="#0f172a">
                  {evData.totalFleetKwh.toLocaleString('id-ID')}{' '}
                  <Typography component="span" variant="h5" fontWeight="700">
                    kWh
                  </Typography>
                </Typography>
              </Stack>
            </Paper>
          </Card>
        </Grid>
      </Grid>

      {/* TREND CHARTS */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              height: '100%'
            }}
          >
            <Typography variant="h6" fontWeight="bold" color="#0f172a" mb={2}>
              {t('revenue.energyUsageTrend', 'Energy Usage Trend')} (Dummy)
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                height: 300
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historyData} margin={{ top: 10, right: 0, left: -25, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} dx={-10} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="kwh" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorUsage)" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Card>
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              height: '100%'
            }}
          >
            <Typography variant="h6" fontWeight="bold" color="#0f172a" mb={2}>
              {t('revenue.costTrend', 'Cost per kWh Trend')} (Dummy)
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                height: 300
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={historyData} margin={{ top: 20, right: 0, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} dx={-10} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    cursor={{ fill: '#f1f5f9' }}
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="cost" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={40}>
                    {historyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#10b981' : '#34d399'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
}
